import { toast } from "sonner";
import { plural } from "./format";

type Action = { label: string; onClick: () => void };

function base(kind: "success" | "error" | "info" | "warning", id: string, message: string, action?: Action) {
  const duration = kind === "error" && action ? Infinity : kind === "success" || kind === "info" ? 4_000 : 8_000;
  toast[kind](message, { id, duration, action });
}

export const toasts = {
  artifactUploaded: (version: string) => base("success", "artifact.uploaded", `Build uploaded and verified · v${version}`),
  artifactInvalid: (reason: string, view: () => void) =>
    base("error", "artifact.invalid", `Build failed verification: ${reason}`, { label: "View details", onClick: view }),
  artifactResolved: (version: string) => base("success", "artifact.resolved", `Released build v${version} downloaded and verified`),
  artifactResolveFailed: (version: string, reason: string, retry: () => void) =>
    base("error", "artifact.resolve_failed", `Couldn't download v${version}: ${reason}`, { label: "Retry", onClick: retry }),
  envPassed: () => base("success", "env.passed", "Environment check passed"),
  envFailed: (n: number, view: () => void) =>
    base("error", "env.failed", `Environment check failed: ${plural(n, "problem")}`, { label: "View", onClick: view }),
  runStarted: (view: () => void) => base("success", "run.started", "Run started", { label: "View run", onClick: view }),
  runActiveConflict: (open: () => void) =>
    base("error", "run.active_conflict", "Another run is already in progress on this machine", { label: "Open active run", onClick: open }),
  runCancelRequested: () => base("info", "run.cancel_requested", "Cancelling run… tests are being cleaned up"),
  runFinished: (passed: number, failed: number, view: () => void) =>
    base(failed ? "error" : "success", "run.finished", `Run finished: ${passed} passed, ${failed} failed`, {
      label: "View report",
      onClick: view,
    }),
  runStateChanged: () => base("info", "run.state_changed", "The run changed while you were acting; showing the latest state"),
  streamReconnected: () => base("success", "stream.reconnected", "Live updates reconnected; missed events replayed"),
  runtimeConnected: () => base("success", "runtime.connected", "Connected to the ATS runtime"),
  reportExported: (format: string) => base("success", "report.exported", `Report downloaded (${format})`),
  jiraSent: (issueKey: string, created: boolean) =>
    base("success", "jira.sent", created ? `Jira issue ${issueKey} created` : `Report added to ${issueKey}`),
  copied: () => base("info", "copy", "Copied"),
  userAdmitted: (name: string, role: string) => base("success", "user.admitted", `${name} admitted as ${role}`),
  userRejected: (email: string) => base("success", "user.rejected", `${email} rejected`),
  userRoleChanged: (name: string, role: string) =>
    base("success", "user.role_changed", `${name} is now ${role}. Takes effect within 5 minutes.`),
  userAccessRemoved: (name: string) => base("success", "user.access_removed", `Access removed for ${name}`),
  userAccessRestored: (name: string, role: string) => base("success", "user.access_restored", `Access restored for ${name} as ${role}`),
  auditExported: (rows: number) => base("success", "audit.exported", `Audit export downloaded (${rows} rows)`),
  settingsSaved: (section: string) => base("success", "settings.saved", `${section} saved`),
  integrationDisconnected: (what: string) => base("success", "integration.disconnected", `${what} disconnected`),
  adminApiUnreachable: (retry: () => void) =>
    base("warning", "admin_api.unreachable", "Can't reach the Admin API", { label: "Retry", onClick: retry }),
  genericError: (message: string, requestId: string) =>
    base(
      "error",
      "generic.error",
      `Something went wrong: ${message}`,
      requestId ? { label: "Copy request ID", onClick: () => void navigator.clipboard.writeText(requestId) } : undefined,
    ),
};

export type ToastKey = keyof typeof toasts;
